import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useAIEmailTemplateCRUDActions } from '@mm/state/actions/AIEmailTemplatesCRUDActions';
import { AIEmailTemplate } from '@mm/state/interfaces/aiEmailTemplates';
import { EmailConfig, WidgetBase } from '@mm/state/model/WidgetModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { StoreState } from '@mm/state/store-interfaces';

const POOLING_INTERVAL = 1000 * 10; // 10 seconds
const POOLING_TIMEOUT = 1000 * 60 * 10; // Stop pooling after 10 minutes

interface Props {
  onAiEmailTemplateLoad: (aiEmailTemplate: AIEmailTemplate) => void;
  widget: WidgetBase<EmailConfig>;
}

export const useAiEmailTemplate = ({
  onAiEmailTemplateLoad,
  widget,
}: Props) => {
  const { processed, canceled, aiEmailTemplate, processing } = useSelector(
    (state: StoreState) => state.aiEmailTemplates,
  );

  const { retrieve, toggleProcessing, cancel } =
    useAIEmailTemplateCRUDActions();
  const activeFacebookPage = useSelector(activeFacebookPageSelector);

  const interval: React.MutableRefObject<null | NodeJS.Timeout> = useRef(null);

  const [widgetProcessed, setWidgetProcessed] = useState<null | boolean>(null);

  // Do not run if widget has own content
  const isWidgetProcessed = () => {
    if (widgetProcessed) return widgetProcessed; // return cached value

    if (!widget) {
      setWidgetProcessed(false);
      return false;
    }

    const { config } = widget;

    let value = false;
    if (!config.aiCustomizer && (config.body || config.subject)) {
      value = true;
    } else if (
      config.aiCustomizer &&
      config.entities &&
      config.entities.length > 0 &&
      config.entities.some(e => e.body || e.subject)
    ) {
      value = true;
    }
    setWidgetProcessed(value);
    return value;
  };

  const stopAiEmailTemplatePooling = () => {
    if (interval.current) {
      clearInterval(interval.current);
      interval.current = null;
    }
  };

  const loadAiEmailTemplate = (fbPageId: number) => {
    if (processed || canceled) return;
    retrieve(fbPageId);
  };

  const startAiEmailTemplatePooling = async (fbPageId: number) => {
    if (processed || canceled || isWidgetProcessed()) {
      return;
    }

    toggleProcessing();
    loadAiEmailTemplate(fbPageId);
    interval.current = setInterval(() => {
      loadAiEmailTemplate(fbPageId);
    }, POOLING_INTERVAL);

    // Stop pooling after timeout
    setTimeout(() => {
      if (processing && !(processed || canceled)) {
        cancel('Timeout');
      }
    }, POOLING_TIMEOUT);
  };

  // Start pooling on mount
  useEffect(() => {
    if (activeFacebookPage?.id) {
      startAiEmailTemplatePooling(activeFacebookPage.id);
    }
    return () => {
      // Clear interval on unmount
      stopAiEmailTemplatePooling();
    };
  }, [activeFacebookPage]);

  // Handle template load
  useEffect(() => {
    if (processed && !canceled && aiEmailTemplate) {
      onAiEmailTemplateLoad(aiEmailTemplate);
    }
  }, [processed, aiEmailTemplate]);

  // Clear interval if processed\\canceled
  useEffect(() => {
    if (processed || canceled) stopAiEmailTemplatePooling();
  }, [processed, canceled]);

  return { cancel };
};
